import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useOfertaCard } from '../../context/ofertas-card-context';
import { getFinanciacionType, getPagoFinanciado, getPagoUnico } from '../../helpers/ofertas-helper';
import { CarlineWithOfertas, Oferta } from '../../services/ofertas-service/models/ofertas';
import { useTrackingManager } from '../../tracking/use-tracking-manager';
import { OfertaCardDesktopAnimated } from './desktop/oferta-card-desktop-animated';
import { CardsContainerDesktop } from './desktop/ui/cards-container-desktop';
import { OfertaGalleryMobile } from './mobile/ofertas-gallery-mobile';

const CardsContainerSelector = ({ isMobile, hasOneCarline, containerRef, children }: any) => {
	if (isMobile) {
		return (
			<div className="ofertas-cards-container-mobile" ref={containerRef}>
				{children}
			</div>
		);
	} else {
		return (
			<CardsContainerDesktop
				hasOneCarline={hasOneCarline}
				className="ofertas-cards-container-desktop"
				ref={containerRef}
			>
				{children}
			</CardsContainerDesktop>
		);
	}
};

const OfertasDesktop = ({ carline }: { carline: CarlineWithOfertas }) => {
	return (
		<>
			{carline.ofertas.map((oferta) => (
				<OfertaCardDesktopAnimated
					className={`oferta-card-desktop-${oferta.id}`}
					carline={carline}
					currentOferta={oferta}
					ofertasNumber={carline.ofertas.length}
				/>
			))}
		</>
	);
};

export const OfertaCardsContainer = ({
	carline,
	isMobile,
}: {
	carline: CarlineWithOfertas;
	isMobile: boolean;
	className: string;
}): React.ReactElement => {
	const containerRef = useRef<HTMLDivElement>(null);

	const [visibleOfertaIndex, setVisibleOfertaIndex] = useState(0);
	const [hasScrolled, setHasScrolled] = useState(false);

	const trackingManager = useTrackingManager();

	const { hasOneCarline } = useOfertaCard();

	const getTracking = useCallback(
		(carline: CarlineWithOfertas, oferta: Oferta) => {
			const pagoFinanciado = getPagoFinanciado(oferta.datosEconomicos);
			trackingManager.trackOfertasElementSwipe({
				CarlineId: carline.code.toString(),
				CarlineName: carline.title,
				SalesgroupName: oferta.carline?.salesgroup.title,
				ModelId: oferta?.configuracion.motorizacion.code.split('-')[0],
				ModelName: oferta.carline?.salesgroup.model.name,
				ModelYear: oferta.carline?.salesgroup.year.toString(),
				PriceRateType: pagoFinanciado ? getFinanciacionType(pagoFinanciado) : undefined,
				PriceRateTotal: pagoFinanciado?.precio ?? undefined,
				PriceRateCurrency: 'EUR',
				PriceTotalTotal: getPagoUnico(oferta.datosEconomicos)?.precio ?? undefined,
				PriceTotalCurrency: 'EUR',
			});
		},
		[trackingManager],
	);

	useEffect(() => {
		const container = containerRef.current;

		if (!container) return;

		const handleScroll = (e: Event) => {
			const target = e.target as HTMLElement;
			const cardWidth = target.scrollWidth / carline.ofertas.length;
			const scrollLeft = target.scrollLeft;

			const visibleCardIndex = Math.round(scrollLeft / cardWidth);
			setVisibleOfertaIndex(visibleCardIndex);

			if (visibleCardIndex === 0) {
				setHasScrolled(false);
			} else {
				setHasScrolled(true);
			}
		};

		container.addEventListener('scroll', handleScroll, { capture: true });

		return () => {
			container.removeEventListener('scroll', handleScroll, { capture: true });
		};
	}, [carline]);

	useEffect(() => {
		if (carline.ofertas.length > 1 && hasScrolled) {
			getTracking(carline, carline.ofertas[visibleOfertaIndex]);
		}
	}, [carline, getTracking, hasScrolled, visibleOfertaIndex]);

	return (
		<CardsContainerSelector isMobile={isMobile} hasOneCarline={hasOneCarline} containerRef={containerRef}>
			{isMobile ? (
				<OfertaGalleryMobile carline={carline} visibleOfertaIndex={visibleOfertaIndex} />
			) : (
				<OfertasDesktop carline={carline} />
			)}
		</CardsContainerSelector>
	);
};
